import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Service } from '../types/service';

// Use relative /api path for Vercel proxy routing, or VITE_API_URL for local development
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add a request interceptor to include the auth token and check expiration
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    const expiryTime = localStorage.getItem('tokenExpiry');
    
    // Check if token exists and is not expired
    if (token && expiryTime) {
      const now = new Date().getTime();
      if (now > parseInt(expiryTime)) {
        // Token expired, clear it and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiry');
        window.location.href = '/';
        return Promise.reject(new Error('Token expired'));
      }
      
      // Add token to request headers
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle 401 Unauthorized responses
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login on unauthorized
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export interface AboutUsContent {
  title: string;
  description: string;
  bottomHeading1: string;
  bottomParagraph1: string;
  bottomHeading2: string;
  bottomParagraph2: string;
  bottomHeading3: string;
  bottomParagraph3: string;
  howWeWork: string;
}

export const aboutUsApi = {
  getContent: async (): Promise<AboutUsContent> => {
    // Server route is mounted at /api/about-us
    const response = await api.get<AboutUsContent>('/api/about-us');
    return response.data;
  },
  
  updateContent: async (content: Partial<AboutUsContent>): Promise<AboutUsContent> => {
    // Server route is mounted at /api/about-us
    const response = await api.put<AboutUsContent>('/api/about-us', content);
    return response.data;
  }
};

export const servicesApi = {
  // Get all services
  getServices: async (): Promise<Service[]> => {
    const response = await api.get<Service[]>('/api/services');
    return response.data;
  },
  
  // Create a new service
  createService: async (serviceData: Omit<Service, '_id' | 'id' | 'createdAt' | 'updatedAt'>): Promise<Service> => {
    const response = await api.post<Service>('/api/services', serviceData);
    return response.data;
  },
  
  // Update a service
  updateService: async (id: string, serviceData: Partial<Omit<Service, '_id' | 'id' | 'createdAt' | 'updatedAt'>>): Promise<Service> => {
    const response = await api.put<Service>(`/api/services/${id}`, serviceData);
    return response.data;
  },
  
  // Delete a service
  deleteService: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/api/services/${id}`);
    return response.data;
  }
};

export default api;
